import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer'
import { Progress } from 'modules/types'
import {
  UserNotification,
  UserNotificationListRequestFilter,
  UserNotificationListRequestSorting,
  UserNotificationsState,
} from 'modules/domain/userNotifications/types'
import { arrToDict, getIds } from 'modules/utils/helpers'
import { LIST_PAGE_SIZE } from 'modules/constants'
import { FetchError } from 'modules/domain/types'

const initialState: UserNotificationsState = {
  items: {},
  meta: {},
  ids: [],

  listFetchProgress: Progress.IDLE,
  listFetchError: null,
  itemFetchProgress: Progress.IDLE,
  itemFetchError: null,

  filter: {},
  sorting: {},
  page: 1,
  total: 0,
  pageSize: LIST_PAGE_SIZE,
}

class UserNotificationsReducer extends ImmerReducer<UserNotificationsState> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  listRequested(params: {
    filter?: UserNotificationListRequestFilter
    sorting?: UserNotificationListRequestSorting
    page?: number
  }) {
    this.draftState.listFetchProgress = Progress.WORK
    this.draftState.listFetchError = null
    this.draftState.filter = params.filter || this.draftState.filter
    this.draftState.sorting = params.sorting || this.draftState.sorting
    this.draftState.page = typeof params.page === 'undefined' ? this.draftState.page : params.page
  }

  listRequestSucceed(list: UserNotification[], total: number, page: number) {
    this.draftState.listFetchProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.page = page
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map((item) => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
  }

  listRequestFailed(error: FetchError) {
    this.draftState.listFetchProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  itemRequested(id: string) {
    this.draftState.itemFetchProgress = Progress.WORK

    const meta = {
      id,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }

    this.draftState.meta[id] = {
      ...meta,
      ...this.draftState.meta[id],
      fetchProgress: Progress.WORK,
      fetchError: null,
    }
  }

  itemRequestSucceed(notification: UserNotification) {
    this.draftState.itemFetchProgress = Progress.SUCCESS
    this.draftState.items[notification.id] = notification

    if (this.draftState.meta[notification.id]) {
      this.draftState.meta[notification.id].fetchProgress = Progress.SUCCESS
      this.draftState.meta[notification.id].fetchError = null
    } else {
      this.draftState.meta[notification.id] = {
        id: notification.id,
        updateProgress: Progress.IDLE,
        updateError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
      }
    }
  }

  itemRequestFailed(id: string, error: FetchError) {
    this.draftState.itemFetchProgress = Progress.ERROR
    if (this.draftState.meta[id]) {
      this.draftState.meta[id].fetchProgress = Progress.ERROR
      this.draftState.meta[id].fetchError = error
    }
  }

  filterUpdated(filter: UserNotificationListRequestFilter) {
    this.draftState.filter = filter
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingUpdated(sorting: UserNotificationListRequestSorting) {
    this.draftState.sorting = sorting
    this.draftState.listFetchProgress = Progress.WORK
  }

  filterHasBeenReset() {
    this.draftState.filter = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingHasBeenReset() {
    this.draftState.sorting = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  dismissRequested(id: string) {
    if (this.draftState.meta[id]) {
      this.draftState.meta[id].removeProgress = Progress.WORK
      this.draftState.meta[id].removeError = null
    } else {
      this.draftState.meta[id] = {
        id,
        fetchProgress: Progress.IDLE,
        fetchError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
        removeProgress: Progress.WORK,
        removeError: null,
      }
    }
  }

  dismissSucceed(id: string) {
    // Remove the notification from items and ids
    if (this.draftState.items[id]) {
      delete this.draftState.items[id]
      this.draftState.ids = this.draftState.ids.filter((itemId) => itemId !== id)
    }
    // Update meta
    if (this.draftState.meta[id]) {
      this.draftState.meta[id].removeProgress = Progress.SUCCESS
      this.draftState.meta[id].removeError = null
    }
    // Update total count
    if (this.draftState.total > 0) {
      this.draftState.total -= 1
    }
  }

  dismissFailed(id: string, error: FetchError) {
    if (this.draftState.meta[id]) {
      this.draftState.meta[id].removeProgress = Progress.ERROR
      this.draftState.meta[id].removeError = error
    }
  }
}

export const UserNotificationActions = createActionCreators(UserNotificationsReducer)
export default UserNotificationActions
export const reducer = createReducerFunction(UserNotificationsReducer, initialState)
